import React, { Dispatch, FC, SetStateAction, useEffect, useState } from "react"
import Image from "next/image"
import { EnvelopeIcon } from "@heroicons/react/24/solid"
import CarrierCostIcon from "@/public/icons/carrier-cost.svg"
import PerMileIcon from "@/public/icons/per-mile.svg"
import PerLoadIcon from "@/public/icons/per-load.svg"
import {
  Days,
  Geo,
  MexicoRates,
  PricingType,
  SalesMexicoMarketResponse,
  SalesUsMarketResponse,
} from "@/lib/types"
import Swal from "sweetalert2"
import { Modal, InputError } from "@/components"
import { useForm } from "react-hook-form"
import { useMutation } from "react-query"
import { api } from "@/lib/api"
import "react-datepicker/dist/react-datepicker.css"
import { ReactMultiEmail } from "react-multi-email"
import "react-multi-email/dist/style.css"
import {
  PlusSmallIcon,
  MinusSmallIcon,
  FlagIcon,
} from "@heroicons/react/24/outline"
import { convertEquipmentType, dateFormat, dollarUS } from "@/lib/services"
import classNames from "classnames"

type Props = {
  day: string
  setday: Dispatch<SetStateAction<string>>
  setToday: Dispatch<SetStateAction<Days>>
  setTomorrow: Dispatch<SetStateAction<Days>>
  activeTab?: "transloadRates" | "directRates" | "spotRates"
  setActiveTab?: Dispatch<
    SetStateAction<"transloadRates" | "directRates" | "spotRates">
  >
  success: boolean
  listingData: Days
  today: Days
  tomorrow: Days
  empty: boolean
  equipment: string
  geoLocations: {
    origin: Geo["geoLocations"]
    destination: Geo["geoLocations"]
  }
  quote: SalesUsMarketResponse | SalesMexicoMarketResponse | undefined
  mexicoRates?: MexicoRates | null
  setDeliveryDate: any
  portOfEntry: string
}

export const Pricing: FC<Props> = (props) => {
  const [email, setEmail] = useState<boolean>(false)
  const [isOpen, setIsOpen] = useState(false)
  const [pickupDate, setPickupDate] = useState(new Date())
  const [deliveryDate, setDeliveryDate] = useState(new Date())
  const [emails, setEmails] = useState<string[]>([])
  const [reportEmails, setReportEmails] = useState<string[]>([])
  const [emailError, setEmailError] = useState("")
  const [reportEmailError, setReportEmailError] = useState("")
  const [transitDays, setTransitDays] = useState(0)
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<PricingType>()
  const {
    register: registerReport,
    handleSubmit: handleSubmitReport,
    reset: resetReport,
  } = useForm<PricingType>()

  const mutation = useMutation(
    (payload: PricingType) => {
      return api.SalesApi.Send(payload)
    },
    {
      onSuccess() {
        setEmail(false)
        Swal.fire({
          icon: "success",
          title: "Successfully Sent Email",
          // background: "#333333",
          toast: true,
          position: "top",
          timer: 2000,
          iconColor: "#CEA16B",
          showCancelButton: false,
          showConfirmButton: false,
          customClass: {
            popup: "custom-success-popup",
          },
        })
      },
      onError(error: { message: string }) {
        Swal.fire({
          icon: "error",
          text: error.message,
          color: "#ffffff",
          background: "#333333",
          toast: true,
          position: "top",
          timer: 2000,
          showCancelButton: false,
          showConfirmButton: false,
        })
      },
    },
  )
  const mutationReport = useMutation(
    (payload: PricingType) => {
      return api.SalesApi.Send(payload)
    },
    {
      onSuccess() {
        setEmail(false)
        Swal.fire({
          icon: "success",
          title: "Successfully Sent Report",
          background: "#333333",
          toast: true,
          position: "top",
          timer: 2000,
          iconColor: "#CEA16B",
          showCancelButton: false,
          showConfirmButton: false,
          customClass: {
            popup: "custom-success-popup",
          },
        })
      },
      onError(error: { message: string }) {
        Swal.fire({
          icon: "error",
          text: error.message,
          color: "#ffffff",
          background: "#333333",
          toast: true,
          position: "top",
          timer: 2000,
          showCancelButton: false,
          showConfirmButton: false,
        })
      },
    },
  )

  const increment = () => {
    const data = { ...props.listingData }
    data.marginPercentage = data.marginPercentage + 0.5
    data.quotePrice = (1 + data.marginPercentage / 100) * data.perLoad
    data.marginGM = data.quotePrice - data.perLoad
    if (props.day == "today") {
      props.setToday(data)
    } else {
      props.setTomorrow(data)
    }
  }
  const decrement = () => {
    const data = { ...props.listingData }
    if (data.marginPercentage && data.perLoad && data.perMile) {
      data.marginPercentage = data.marginPercentage - 0.5
      data.quotePrice = (1 + data.marginPercentage / 100) * data.perLoad
      data.marginGM = data.quotePrice - data.perLoad
      if (props.day == "today") {
        props.setToday(data)
      } else {
        props.setTomorrow(data)
      }
    }
  }

  const onSubmit = (data: PricingType) => {
    data.report = false
    data.email = emails
    data.portOfEntry = props.portOfEntry

    if (props.quote && "data" in props.quote) {
      data.mileage = props.quote.data.carrierCostData.mileage ?? 0
      data.transitDays = props.today.transitDays =
        props.quote.data.costModifiers.today.transitDays ?? 0
      data.transitDays = props.tomorrow.transitDays =
        props.quote.data.costModifiers.tomorrow.transitDays ?? 0
    } else if (props.quote && "mexicoRates" in props.quote) {
      data.mileage = props.quote.quoteData.location.mileage?.total ?? 0
      data.transitDays =
        props.quote.mexicoRates.transloadRates.dates[0].adjCarrierCost
          .transitDays ?? 0
    }

    if (data.email.length == 0) {
      setEmailError("Please select one or more emails.")
    } else if (pickupDate && deliveryDate) {
      data.pickupDate = dateFormat(pickupDate).split("T")[0]
      data.deliveryDate = dateFormat(deliveryDate).split("T")[0]
      mutation.mutate(data)
    }
  }

  const onSubmitReport = (data: PricingType) => {
    if (props.quote && "data" in props.quote) {
      const mileage = props.quote.data.carrierCostData.mileage || 0
      data.mileage = mileage
      if (props.day === "today") {
        const transitToday = props.quote.data.costModifiers.today.transitDays
        if (transitToday) {
          data.transitDays = transitToday
        }
      } else if (props.day === "tomorrow") {
        const transitTomorrow =
          props.quote.data.costModifiers.tomorrow.transitDays
        if (transitTomorrow) {
          data.transitDays = transitTomorrow
        }
      }
    } else if (props.quote && "mexicoRates" in props.quote) {
      data.mileage = props.quote.quoteData.location.mileage?.total ?? 0
      data.transitDays =
        props.quote.mexicoRates.transloadRates.dates[0].adjCarrierCost
          .transitDays ?? 0
    }
    data.report = true
    data.portOfEntry = props.portOfEntry
    data.deliveryDate = dateFormat(deliveryDate).split("T")[0]
    data.destination = `${props.geoLocations.destination.city}, ${props.geoLocations.destination.state} ${props.geoLocations.destination.zip}`
    data.email = ["support@broncotrans.com", ...reportEmails]
    data.equipmentType = convertEquipmentType(props.equipment)
    data.origin = `${props.geoLocations.origin.city}, ${props.geoLocations.origin.state} ${props.geoLocations.origin.zip}`
    data.pickupDate = dateFormat(pickupDate).split("T")[0]
    data.quotePrice = dollarUS.format(props.listingData.quotePrice)

    if (data.email.length == 0) {
      setReportEmailError("Please select one or more emails.")
    } else {
      mutationReport.mutate(data)
      setIsOpen(false)
    }
  }

  const populateData = () => {
    setEmail(true)
    setValue(
      "origin",
      `${props.geoLocations.origin.city}, ${props.geoLocations.origin.state} ${props.geoLocations.origin.zip}`,
    )
    setValue(
      "destination",
      `${props.geoLocations.destination.city}, ${props.geoLocations.destination.state} ${props.geoLocations.destination.zip}`,
    )
    setValue("equipmentType", convertEquipmentType(props.equipment))
    setValue("quotePrice", dollarUS.format(props.listingData.quotePrice))
  }

  useEffect(() => {
    if (!props.quote) return

    const updateDates = (dayIndex: number) => {
      if (!props.quote) return

      let pickupDate = ""
      let deliveryDate = ""
      let pickupDateObject = new Date()
      let deliveryDateObject = new Date()

      if ("data" in props.quote) {
        pickupDate = props.quote.data.costModifiers.today.pickUpDate || ""
        deliveryDate = props.quote.data.costModifiers.today.dropOffDate || ""
        pickupDateObject = new Date(pickupDate)
        deliveryDateObject = new Date(deliveryDate)
      }

      if (
        "mexicoRates" in props.quote &&
        props.activeTab &&
        props.quote.mexicoRates[props.activeTab].dates != undefined
      ) {
        const adjCarrierCost =
          props.quote.mexicoRates[props.activeTab].dates[dayIndex]
            .adjCarrierCost

        pickupDate = adjCarrierCost.pickUpDate || ""
        deliveryDate = adjCarrierCost.dropOffDate || ""
        pickupDateObject = new Date(pickupDate)
        deliveryDateObject = new Date(deliveryDate)
      }

      setPickupDate(pickupDateObject)
      setDeliveryDate(deliveryDateObject)
      props.setDeliveryDate(deliveryDateObject)
    }

    if (props.day === "today") {
      updateDates(0)
    } else if (props.day === "tomorrow") {
      updateDates(1)
    }
  }, [props.day, props.quote, props.today, props.tomorrow])

  const isEmpty = (object: Days) => {
    const hasUndefined = Object.values(object).some(
      (value) => value === undefined,
    )
    return hasUndefined
  }

  useEffect(() => {
    if (isEmpty(props.today) == true) {
      props.setday("tomorrow")
    }
  }, [props.today])

  const handleReportEmail = (emails: string[]) => {
    setReportEmails(
      emails.filter((email) => email !== "support@broncotrans.com"),
    )
  }

  return (
    <>
      <Modal
        open={email}
        close={() => setEmail(false)}
        width="small"
        title="Email"
      >
        <div className="flex flex-col gap-3">
          <div>
            <h5 className="text-xs dark:text-primary-8 text-light-1">Email</h5>
            <ReactMultiEmail
              placeholder="To..."
              emails={emails}
              onChange={(_emails: string[]) => {
                setEmailError("")
                setEmails(_emails)
              }}
              autoFocus={true}
              getLabel={(email, index, removeEmail) => {
                return (
                  <div data-tag key={index}>
                    <div data-tag-item>{email}</div>
                    <span data-tag-handle onClick={() => removeEmail(index)}>
                      ×
                    </span>
                  </div>
                )
              }}
            />
            {emailError !== "" && (
              <div className="pt-0.5">
                <InputError message={emailError} />
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-x-3">
            <fieldset className="pt-1">
              <label
                className="text-xs dark:text-primary-8 text-light-1"
                htmlFor=""
              >
                Origin
              </label>
              <input
                className={
                  (errors.origin
                    ? "border-red-400"
                    : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent bg-white outline-none dark:text-white text-light-2 border-2 rounded"
                }
                type="text"
                {...register("origin", { required: true })}
              />
            </fieldset>
            <fieldset className="pt-1">
              <label
                className="text-xs dark:text-primary-8 text-light-1"
                htmlFor=""
              >
                Destination
              </label>
              <input
                className={
                  (errors.destination
                    ? "border-red-400"
                    : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent bg-white outline-none dark:text-white text-light-2 border-2 rounded"
                }
                type="text"
                {...register("destination", { required: true })}
              />
            </fieldset>
          </div>
          <div className="grid grid-cols-2 gap-x-3">
            <fieldset className="pt-1">
              <label
                className="text-xs dark:text-primary-8 text-light-1"
                htmlFor=""
              >
                Equipment Type
              </label>
              <input
                className={
                  (errors.equipmentType
                    ? "border-red-400"
                    : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent bg-white outline-none dark:text-white text-light-2 border-2 rounded"
                }
                type="text"
                {...register("equipmentType", { required: true })}
              />
            </fieldset>
            <fieldset className="pt-1">
              <label
                className="text-xs dark:text-primary-8 text-light-1"
                htmlFor=""
              >
                Quote Price
              </label>
              <input
                className={
                  (errors.quotePrice
                    ? "border-red-400"
                    : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent bg-white outline-none dark:text-white text-light-2 border-2 rounded"
                }
                type="text"
                {...register("quotePrice", { required: true })}
              />
            </fieldset>
          </div>
          <button
            className="bg-primary-8 px-4 py-2 rounded font-medium w-full"
            onClick={handleSubmit(onSubmit)}
            type="button"
          >
            Send Quote
          </button>
        </div>
      </Modal>
      <Modal
        open={isOpen}
        close={() => setIsOpen(false)}
        width="x-small"
        title="Email"
      >
        <div className="pt-4 flex flex-col gap-3">
          <div>
            <h5 className="text-xs text-accent pb-1">Email</h5>
            <ReactMultiEmail
              placeholder="To..."
              emails={["support@broncotrans.com", ...reportEmails]}
              onChange={handleReportEmail}
              autoFocus={true}
              getLabel={(
                email: string,
                index: number,
                removeEmail: (index: number) => void,
              ) => {
                return (
                  <div data-tag key={index}>
                    <div data-tag-item>{email}</div>
                    {email !== "support@broncotrans.com" && (
                      <span data-tag-handle onClick={() => removeEmail(index)}>
                        ×
                      </span>
                    )}
                  </div>
                )
              }}
            />
            {reportEmailError !== "" && (
              <div className="pt-0.5">
                <InputError message={reportEmailError} />
              </div>
            )}
          </div>
          <fieldset className="flex flex-col gap-1">
            <label className="text-xs dark:text-primary-8 text-light-1">
              Notes
            </label>
            <textarea
              className="w-full text-sm p-2 dark:bg-transparent bg-white outline-none dark:text-white text-light-2 border dark:border-secondary-2rounded drop-shadow resize-none placeholder:text-xs"
              placeholder="Add Notes here..."
              rows={8}
              {...registerReport("notes")}
            />
          </fieldset>

          <button
            className="bg-primary-8 px-4 py-2 rounded font-medium w-full"
            onClick={handleSubmitReport(onSubmitReport)}
            type="button"
          >
            Send Quote
          </button>
        </div>
      </Modal>
      <div
        className={
          (props.success ? "" : "opacity-50") +
          " relative lg:col-span-5 col-span-12 "
        }
      >
        <div
          className={
            (props.success ? "hidden" : "block") +
            " absolute w-full h-full z-10 bg-blak"
          }
        ></div>
        <div className="2xl:flex xl:block flex flex-wrap gap-10 items-center mb-5">
          <h1 className="dark:text-primary-10 text-light-2 2xl:text-28 text-2xl">
            Pricing
          </h1>
          {props.activeTab && props.quote && "mexicoRates" in props.quote && (
            <ul className="2xl:w-fit xl:w-full dark:bg-primary-1 w-fit ml-auto flex justify-end dark:text-white border-0 mt-2 border-b-4 pb-3 dark:border-secondary-3  ">
              {props.mexicoRates?.transloadRates && (
                <li
                  className="-mb-px mr-1"
                  onClick={() =>
                    props.setActiveTab && props.setActiveTab("transloadRates")
                  }
                >
                  <a
                    className={classNames(
                      `px-5 pb-3 dark:text-primary-10 text-light-2`,
                      true,
                      props.activeTab === "transloadRates"
                        ? "border-0 border-b-4 border-primary-3"
                        : "",
                    )}
                    href="#"
                  >
                    Transload Rates
                  </a>
                </li>
              )}
              {props.mexicoRates?.spotRates
                ? null
                : props.mexicoRates?.directRates && (
                    <li
                      className="-mb-px mr-1"
                      onClick={() =>
                        props.setActiveTab && props.setActiveTab("directRates")
                      }
                    >
                      <a
                        className={classNames(
                          `px-5 pb-3 dark:text-primary-10 text-light-2`,
                          true,
                          props.activeTab === "directRates"
                            ? "border-0 border-b-4 border-primary-3"
                            : "",
                        )}
                        href="#"
                      >
                        Direct Rates
                      </a>
                    </li>
                  )}
              {props.mexicoRates?.spotRates && (
                <li
                  className="-mb-px mr-1"
                  onClick={() =>
                    props.setActiveTab && props.setActiveTab("spotRates")
                  }
                >
                  <a
                    className={classNames(
                      `px-5 pb-3`,
                      true,
                      props.activeTab === "spotRates"
                        ? "border-0 border-b-4 border-primary-3 dark:text-primary-10 text-light-2"
                        : "",
                    )}
                    href="#"
                  >
                    {props.mexicoRates?.spotRates && "Direct Rates"}
                  </a>
                </li>
              )}
            </ul>
          )}
        </div>
        <div className="dark:bg-primary-1 bg-white border-2 dark:border-secondary-11 rounded-xl mt-4">
          <div className="flex justify-between border-b-2 dark:border-secondary-11 px-4">
            <h1 className="dark:text-primary-10 text-light-2 2xl:text-22 text-lg py-3 w-full text-center">
              Customer Quote
            </h1>
            <div className="flex gap-2 items-center text-accent">
              <EnvelopeIcon
                tabIndex={7}
                className="w-7 cursor-pointer"
                onClick={() => {
                  setEmails([])
                  populateData()
                }}
              />
              <FlagIcon
                tabIndex={6}
                className="w-7 cursor-pointer"
                onClick={() => {
                  resetReport({
                    notes: "",
                  })
                  setReportEmails([])
                  setIsOpen(true)
                }}
              />
            </div>
          </div>
          <div className="flex justify-evenly dark:gap-1 dark:bg-secondary-2 bg-[#ebc9a4] dark:border-2 dark:border-secondary-11 rounded-md m-6">
            <div className="flex-1 ">
              <input
                className={`hidden pricing_radio_customize`}
                type="radio"
                name="customer_quote"
                id="customer_quote"
                checked={props.day == "today" ? true : false}
                onChange={() => props.setday("today")}
                disabled={
                  isEmpty(props.today) == true || props.empty == true
                    ? true
                    : false
                }
              />
              <label
                tabIndex={7}
                className="cursor-pointer dark:bg-transparent dark:text-primary-2 text-gray-700 2xl:text-22 w-full block text-center 2xl:p-4 md:p-3 p-2 md:text-lg text-base font-normal"
                htmlFor="customer_quote"
              >
                Today
              </label>
            </div>
            <div className="flex-1">
              <input
                className={`hidden pricing_radio_customize`}
                type="radio"
                name="customer_quote"
                id="customer_quote1"
                checked={props.day == "tomorrow" ? true : false}
                onChange={() => props.setday("tomorrow")}
              />
              <label
                tabIndex={8}
                className="cursor-pointer dark:bg-transparent 2xl:text-22 w-full block text-center 2xl:p-4 md:p-3 p-2 md:text-lg text-base font-normal dark:text-primary-2 text-gray-700"
                htmlFor="customer_quote1"
              >
                Tomorrow
              </label>
            </div>
          </div>
          <div className="grid grid-cols-3 place-items-center pb-5">
            <button
              tabIndex={9}
              onClick={() => decrement()}
              disabled={props.listingData.marginPercentage < 1.5}
              type="button"
            >
              <MinusSmallIcon className="w-12 dark:text-primary-10 text-primary-1 dark:bg-secondary-11 bg-primary-3 rounded-full p-3" />
            </button>
            <div className="">
              <h1 className="text-accent 2xl:text-2xl md:text-22 text-lg text-center">
                {dollarUS.format(props.listingData.quotePrice)}
              </h1>
              <h6 className="dark:text-primary-10 text-light-2 md:text-lg text-base text-center pt-2">
                {dollarUS.format(props.listingData.marginGM)} GM (
                {props.listingData.marginPercentage}%)
              </h6>
            </div>
            <button
              tabIndex={10}
              onClick={() => increment()}
              disabled={props.listingData.marginPercentage > 99.5}
              type="button"
            >
              <PlusSmallIcon className="w-12 dark:text-primary-10 text-primary-1 dark:bg-secondary-11 bg-primary-3 shadow-lg rounded-full p-3" />
            </button>
          </div>
        </div>
        <div className="dark:bg-primary-1 bg-white border-2 dark:border-secondary-11 rounded-xl mt-4">
          <div className="grid grid-cols-7 gap-4 p-4 md:divide-x dark:divide-secondary-11">
            <div className="md:col-span-3 col-span-12 flex gap-2 items-center ">
              <div className="dark:bg-secondary-13 bg-transparent  rounded-full h-10 w-10 relative">
                <Image
                  className="w-5 h-5 absolute right-[7px] top-[9px]"
                  src={CarrierCostIcon.src}
                  width={100}
                  height={100}
                  alt="carrier-cost-icon"
                />
              </div>
              <h1 className="dark:text-primary-10 text-light-2 py-3">
                Estimated Carrier Cost
              </h1>
            </div>
            <div className="md:col-span-2 col-span-12 flex gap-2 items-center px-2">
              <div className="dark:bg-secondary-13 rounded-full h-10 w-10 relative">
                <Image
                  className="w-6 h-6 absolute right-[6px] top-[7px]"
                  src={PerMileIcon.src}
                  width={100}
                  height={100}
                  alt="per-mile-icon"
                />
              </div>
              <div>
                <p className="dark:text-primary-10 text-light-2">
                  {dollarUS.format(props.listingData.perMile)}
                </p>
                <p className="text-accent pt-1">Per Mile</p>
              </div>
            </div>
            <div className="md:col-span-2 col-span-12 flex gap-2 items-center ">
              <div className="dark:bg-secondary-13 rounded-full h-10 w-10 relative">
                <Image
                  className="w-5 h-5 absolute right-[8px] top-[11px]"
                  src={PerLoadIcon.src}
                  width={100}
                  height={100}
                  alt="per-load-icon"
                />
              </div>
              <div>
                <p className="dark:text-primary-10 text-light-2">
                  {dollarUS.format(props.listingData.perLoad)}
                </p>
                <p className="text-accent pt-1">Per Load</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
